import React, { createContext, useReducer, useContext, ReactNode } from "react";

// Define types for state and actions
type SharedState = Record<string, any>;

interface SetValueAction {
  type: "SET_VALUE";
  key: string;
  value: any;
}

interface RemoveValueAction {
  type: "REMOVE_VALUE";
  key: string;
}

interface SetCurrentStepAction {
  type: "SET_CURRENT_STEP";
  step: number;
}

type SharedStateAction =
  | SetValueAction
  | RemoveValueAction
  | SetCurrentStepAction;

function sharedStateReducer(
  state: SharedState,
  action: SharedStateAction
): SharedState {
  switch (action.type) {
    case "SET_VALUE":
      return { ...state, [action.key]: action.value };
    case "REMOVE_VALUE":
      const { [action.key]: _, ...rest } = state;
      return rest;
    case "SET_CURRENT_STEP":
      return { ...state, currentStep: action.step };
    default:
      throw new Error(
        `Unhandled action type: ${(action as SharedStateAction).type}`
      );
  }
}

// Create Contexts
const SharedStateContext = createContext<SharedState | undefined>(undefined);
const SharedStateDispatchContext = createContext<
  React.Dispatch<SharedStateAction> | undefined
>(undefined);

// Provider component
interface SharedStateProviderProps {
  children: ReactNode;
}

export const SharedStateProvider: React.FC<SharedStateProviderProps> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(sharedStateReducer, { currentStep: 0 });

  return (
    <SharedStateContext.Provider value={state}>
      <SharedStateDispatchContext.Provider value={dispatch}>
        {children}
      </SharedStateDispatchContext.Provider>
    </SharedStateContext.Provider>
  );
};

// Custom hooks for consuming the contexts
export const useSharedState = () => {
  const context = useContext(SharedStateContext);
  if (context === undefined) {
    throw new Error("useSharedState must be used within a SharedStateProvider");
  }
  return context;
};

export const useSharedStateDispatch = () => {
  const context = useContext(SharedStateDispatchContext);
  if (context === undefined) {
    throw new Error(
      "useSharedStateDispatch must be used within a SharedStateProvider"
    );
  }
  return context;
};
